// ============================================================================
// agoraCodes.js — Codes de vote Agora : le client ne les connaît plus
// ============================================================================
// Fermeture de la fuite ① de l'audit du 27/08/2026. `sessions/current` est
// lisible sans compte et portait `participants[].code` en clair : n'importe
// quel visiteur lisait les codes et se présentait comme un participant.
//
// Désormais :
//   • le document public ne porte qu'un drapeau `aCode` (booléen, non secret) ;
//   • les codes vivent dans `agoraCodes/{sessionId}`, fermée au client ;
//   • le participant fait VÉRIFIER son code par le serveur (agoraVerifierCode,
//     limité par adresse et verrouillé par séance contre la force brute) ;
//   • l'administration lit et dépose les codes par Cloud Function ;
//   • le lien nominatif de vote porte un jeton opaque (agoraIdentifierParLien).
// Le temps que les clients restés sur l'ancien code se rafraîchissent, un
// `code` encore présent sur un participant est toléré (aUnCode).
// ============================================================================

import { regionFonction } from "./regionsFonctions";

export const aUnCode = (p) => !!p && (p.aCode === true || !!String(p.code || "").trim());

// Codes historiques : cinq chiffres (genCode). Le serveur tolère 4 à 8.
export const estCodeComplet = (c) => /^\d{5,8}$/.test(String(c || "").replace(/\s+/g, ""));

export const genCode = () => Math.floor(10000 + Math.random() * 90000).toString();

async function cf(name, data, timeout = 30000) {
  const { getFunctions, httpsCallable } = await import("firebase/functions");
  const fn = getFunctions(undefined, regionFonction(name));
  try {
    const res = await httpsCallable(fn, name, { timeout })(data);
    return res.data;
  } catch (e) {
    // Mode « strict » (securiteConfig/authEnforcement) : un appel sans jeton
    // reçoit un défi → bannière de reconnexion, puis l'erreur suit son cours.
    try { const g = await import("./authGuard"); g.signalerSiDefi(e); } catch {}
    throw e;
  }
}

/** Vérifie un code auprès du serveur. `identite` = { prenom, nom, mutuelle }
 *  (facultatif : sans identité, le code doit désigner un participant unique).
 *  Réponse : { ok, participant?: {id, prenom, nom, mutuelle}, motif? }. */
export const verifierCode = (sessionId, code, identite = {}) =>
  cf("agoraVerifierCode", { sessionId, code: String(code || "").replace(/\s+/g, ""), ...identite });

/** Lien nominatif de vote (jeton `t` de l'URL) → identité + code. */
export const identifierParLien = (token) => cf("agoraIdentifierParLien", { token });

/** Administration : codes d'une séance ({ [participantId]: code }). */
export const lireCodes = async (sessionId, aramisUserId) =>
  (await cf("agoraLireCodes", { sessionId, aramisUserId })).codes || {};

/** Administration : dépose des codes et/ou en retire. */
export const enregistrerCodes = (sessionId, codes, aramisUserId, retirer = []) =>
  cf("agoraEnregistrerCodes", { sessionId, codes: codes || {}, retirer, aramisUserId });

/** Administration : lien nominatif de vote (jeton opaque), à copier. */
export const lienVoteNominatif = async (sessionId, participantId, aramisUserId, appUrl) =>
  (await cf("agoraLienVote", { sessionId, participantId, aramisUserId, appUrl })).url;

/** API que les effets statutaires (cooptation, démission) reçoivent pour
 *  retirer ou attribuer un code sans jamais l'écrire dans le document public. */
export const creerCodesApi = (sessionId, aramisUserId) => ({
  enregistrer: (codes) => enregistrerCodes(sessionId, codes, aramisUserId),
  retirer: (ids) => enregistrerCodes(sessionId, {}, aramisUserId, ids),
});
